/**
 * i18n slice — mirrors `src/core/i18n/translations.ts` in the user's repo.
 * The Translations workspace reads from here and writes back through
 * `updateFiles` (the parent handles persistence via the i18n codec).
 */
import isEqual from 'lodash/isEqual';

import type { I18nData } from '@/ast/readers/i18n/parse';
import type { RootState } from '@/store/rootState';

export interface I18nState {
  data: I18nData | null;
  source: string | null;
  error: string | null;
  synced: boolean;
}

const i18nInitial: I18nState = { data: null, source: null, error: null, synced: false };

export type I18nAction = {
  type: 'I18N_SET';
  data: I18nData | null;
  source: string | null;
  error: string | null;
};

export function i18n(state: I18nState = i18nInitial, action: any): I18nState {
  switch ((action as I18nAction).type) {
    case 'I18N_SET': {
      const a = action as Extract<I18nAction, { type: 'I18N_SET' }>;
      if (
        state.synced &&
        state.source === a.source &&
        state.error === a.error &&
        isEqual(state.data, a.data)
      )
        return state;
      return { data: a.data, source: a.source, error: a.error, synced: true };
    }
    default:
      return state;
  }
}

export const setI18n = (
  data: I18nData | null,
  source: string | null,
  error: string | null = null,
): I18nAction => ({
  type: 'I18N_SET',
  data,
  source,
  error,
});

export const selectI18n = (state: RootState) => state.i18n.data;
export const selectI18nSynced = (state: RootState) => state.i18n.synced;
export const selectI18nError = (state: RootState) => state.i18n.error;
